/**
 * Strategy Call & 100% Refundable Deposit — the send popup.
 *
 * Opened from the "Strategy Call" button beside "New VEXOS Quote" on the Saved
 * Quotes page. Three fields the rep actually has to hand (client name, email,
 * project), and one action that sends the DocuSign addendum, mints the $5,000
 * pay link, and emails the client both.
 *
 * The success panel is not a toast on purpose: the two links it shows are the
 * durable ones a rep will want to paste into a text or a Slack DM when a client
 * says "resend that to me", and a toast takes them away after four seconds.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X,
  Loader2,
  CheckCircle2,
  Copy,
  Check,
  AlertTriangle,
  FileSignature,
  CreditCard,
  Mail,
  RefreshCw,
  Landmark,
} from "lucide-react";
import { apiClient } from "../../App";

const DEPOSIT_LABEL = "$5,000";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/** Sent → Signed → Paid, as three independent facts rather than one status:
 *  a client can pay before signing, and a rep needs to see which is missing. */
function StagePill({ label, done, tone = "estate" }) {
  const palette = done
    ? tone === "estate"
      ? { bg: "rgba(143,167,155,0.14)", color: "#8FA79B", border: "rgba(143,167,155,0.38)" }
      : { bg: "rgba(10,138,74,0.14)", color: "#3ECF83", border: "rgba(60,201,122,0.35)" }
    : { bg: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.4)", border: "rgba(255,255,255,0.1)" };
  return (
    <span
      style={{
        fontSize: "10px",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        padding: "3px 9px",
        borderRadius: "999px",
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

function CopyLink({ icon: Icon, label, url, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select the link and copy it by hand");
    }
  };
  return (
    <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-lg px-3.5 py-3">
      <Icon className="w-4 h-4 shrink-0 text-[#8FA79B]" />
      <div className="min-w-0 flex-1">
        <p className="text-white/80" style={{ fontSize: "12px" }}>
          {label}
        </p>
        <p className="truncate text-white/35" style={{ fontSize: "11px" }} title={url}>
          {url}
        </p>
      </div>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-colors"
        style={{ fontSize: "11px" }}
        data-testid={testId}
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-white/55 mb-1.5" style={{ fontSize: "11.5px" }}>
        {label}
      </span>
      {children}
      {hint ? (
        <span className="block text-white/30 mt-1.5" style={{ fontSize: "11px" }}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}

const inputClass =
  "w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#8FA79B] transition-colors";

export default function StrategyCallModal({ open, onClose }) {
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [projectName, setProjectName] = useState("");
  const [company, setCompany] = useState("");
  const [ccRaw, setCcRaw] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [confirmingWireId, setConfirmingWireId] = useState(null);

  const loadRecent = useCallback(async () => {
    setLoadingRecent(true);
    try {
      const res = await apiClient.get("/strategy-calls", { params: { limit: 8 } });
      setRecent(Array.isArray(res.data) ? res.data : []);
    } catch {
      // A failed history load must not look like a failed send — the form
      // above it still works, so this stays silent.
      setRecent([]);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  // Reset to a clean form every time the popup opens, so a second send never
  // inherits the previous client's details.
  useEffect(() => {
    if (!open) return;
    setClientName("");
    setClientEmail("");
    setProjectName("");
    setCompany("");
    setCcRaw("");
    setSendEmail(true);
    setSending(false);
    setResult(null);
    loadRecent();
  }, [open, loadRecent]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, sending, onClose]);

  const ccEmails = useMemo(
    () =>
      ccRaw
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [ccRaw]
  );
  const ccInvalid = ccEmails.filter((e) => !EMAIL_RE.test(e));

  const emailValid = EMAIL_RE.test(clientEmail.trim());
  const canSend =
    !sending &&
    clientName.trim().length > 0 &&
    emailValid &&
    projectName.trim().length > 0 &&
    ccInvalid.length === 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSend) return;
    setSending(true);
    try {
      const { data } = await apiClient.post("/strategy-calls", {
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
        project_name: projectName.trim(),
        company: company.trim() || null,
        cc_emails: ccEmails.length ? ccEmails : null,
        send_email: sendEmail,
      });
      setResult(data);
      if (sendEmail && data?.email?.sent === false) {
        // The envelope went out; only the covering email failed. Say which.
        toast.warning("Addendum sent — the covering email failed to send");
      } else {
        toast.success("Strategy Call package sent");
      }
      loadRecent();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Failed to send the package";
      toast.error(typeof detail === "string" ? detail : "Failed to send the package");
    } finally {
      setSending(false);
    }
  };

  // A wire cannot settle itself — no bank calls a webhook — so this is the
  // only way a wire-paid package reaches "paid".
  const confirmWire = async (id) => {
    setConfirmingWireId(id);
    try {
      const { data } = await apiClient.post(`/strategy-calls/${id}/confirm-wire`, {});
      toast.success(data?.already_paid ? "Already marked paid" : "Wire confirmed — deposit marked paid");
      loadRecent();
      // Keep the success panel in step when it is this package on screen.
      setResult((r) => (r && r.id === id ? { ...r, ...data, id } : r));
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to confirm the wire";
      toast.error(typeof detail === "string" ? detail : "Failed to confirm the wire");
    } finally {
      setConfirmingWireId(null);
    }
  };

  const resend = async (id) => {
    setResendingId(id);
    try {
      await apiClient.post(`/strategy-calls/${id}/resend-email`);
      toast.success("Email resent");
      loadRecent();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to resend the email";
      toast.error(typeof detail === "string" ? detail : "Failed to resend the email");
    } finally {
      setResendingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      onClick={() => { if (!sending) onClose(); }}
      data-testid="strategy-call-modal"
    >
      <div
        className="relative w-full max-w-[600px] my-6 bg-[#080808] border border-white/12 rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 sm:px-7 pt-6 pb-5 border-b border-white/[0.07]">
          <div>
            <p
              className="uppercase text-[#8FA79B] mb-2"
              style={{ fontSize: "10px", letterSpacing: "0.22em" }}
            >
              The Render Pros
            </p>
            <h2
              className="text-white font-light"
              style={{ fontFamily: "var(--font-sans)", fontSize: "22px", letterSpacing: "-0.3px" }}
            >
              Strategy Call
            </h2>
            <p className="text-white/40 mt-1.5" style={{ fontSize: "12.5px" }}>
              {DEPOSIT_LABEL} fully refundable deposit · wire or card · one send
            </p>
          </div>
          <button
            type="button"
            onClick={() => { if (!sending) onClose(); }}
            className="shrink-0 text-white/40 hover:text-white transition-colors p-1 -mt-1 -mr-1"
            aria-label="Close"
            data-testid="strategy-call-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          /* ── Sent ─────────────────────────────────────────────────── */
          <div className="px-6 sm:px-7 py-6" data-testid="strategy-call-result">
            <div className="flex items-start gap-3 mb-5">
              <CheckCircle2 className="w-5 h-5 text-[#3ECF83] shrink-0 mt-0.5" />
              <div>
                <p className="text-white" style={{ fontSize: "15px" }}>
                  Package sent to {result.client_name}
                </p>
                <p className="text-white/45 mt-1" style={{ fontSize: "12.5px" }}>
                  DocuSign has emailed {result.client_email} the addendum to sign.
                  {result.email_sent
                    ? " Our covering email with both links went out too."
                    : ""}
                </p>
              </div>
            </div>

            {result.email_sent === false && result.email_error ? (
              <div className="flex items-start gap-2.5 mb-5 rounded-lg px-3.5 py-3 border"
                style={{ background: "rgba(194,74,46,0.10)", borderColor: "rgba(194,74,46,0.35)" }}
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#E08A6B" }} />
                <div>
                  <p style={{ fontSize: "12.5px", color: "#E9B49E" }}>
                    The covering email did not send. The addendum still went out from DocuSign.
                  </p>
                  <p className="mt-1" style={{ fontSize: "11.5px", color: "rgba(233,180,158,0.7)" }}>
                    {result.email_error}
                  </p>
                  <button
                    type="button"
                    onClick={() => resend(result.id)}
                    disabled={resendingId === result.id}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/20 text-white/80 hover:border-white/40 transition-colors disabled:opacity-50"
                    style={{ fontSize: "11.5px" }}
                    data-testid="strategy-call-retry-email"
                  >
                    {resendingId === result.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    Try the email again
                  </button>
                </div>
              </div>
            ) : null}

            <p className="text-white/45 mb-3" style={{ fontSize: "11.5px" }}>
              Both links stay valid — each one opens a fresh DocuSign or Stripe session
              when the client clicks it, so they are safe to resend at any time.
            </p>
            <div className="space-y-2.5">
              <CopyLink
                icon={FileSignature}
                label="Sign the addendum"
                url={result.sign_url}
                testId="strategy-call-copy-sign"
              />
              <CopyLink
                icon={CreditCard}
                label={`Pay the ${DEPOSIT_LABEL} deposit — wire or card`}
                url={result.pay_url}
                testId="strategy-call-copy-pay"
              />
            </div>

            <div className="flex flex-wrap gap-2.5 mt-6">
              <button
                type="button"
                onClick={() => setResult(null)}
                className="px-5 py-2.5 rounded-full transition-all"
                style={{ background: "#8FA79B", color: "#000000", fontSize: "12.5px" }}
                data-testid="strategy-call-send-another"
              >
                Send another
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-full border border-white/15 text-white/70 hover:border-white/30 hover:text-white transition-colors"
                style={{ fontSize: "12.5px" }}
                data-testid="strategy-call-done"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* ── Form ─────────────────────────────────────────────────── */
          <form onSubmit={submit} className="px-6 sm:px-7 py-6" data-testid="strategy-call-form">
            <div className="space-y-4">
              <Field label="Client name">
                <input
                  autoFocus
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Jordan Reyes"
                  className={inputClass}
                  data-testid="strategy-call-client-name"
                />
              </Field>
              <Field
                label="Client email"
                hint={
                  clientEmail.trim() && !emailValid
                    ? "That does not look like an email address."
                    : "DocuSign sends the addendum here, and so does our covering email."
                }
              >
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="jordan@example.com"
                  className={inputClass}
                  data-testid="strategy-call-client-email"
                />
              </Field>
              <Field label="Project name">
                <input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Solara Residences"
                  className={inputClass}
                  data-testid="strategy-call-project-name"
                />
              </Field>
              <Field
                label="Company (optional)"
                hint="Printed on the addendum. Left blank, the client fills it in when they sign."
              >
                <input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Solara Development Group LLC"
                  className={inputClass}
                  data-testid="strategy-call-company"
                />
              </Field>
              <Field
                label="CC on the signed copy (optional)"
                hint={
                  ccInvalid.length
                    ? `Not a valid address: ${ccInvalid.join(", ")}`
                    : "Comma-separated. They receive the completed packet but never sign."
                }
              >
                <input
                  value={ccRaw}
                  onChange={(e) => setCcRaw(e.target.value)}
                  placeholder="assistant@example.com, counsel@example.com"
                  className={inputClass}
                  data-testid="strategy-call-cc"
                />
              </Field>
            </div>

            <label className="flex items-start gap-2.5 mt-5 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="mt-0.5 accent-[#8FA79B]"
                data-testid="strategy-call-send-email"
              />
              <span className="text-white/60" style={{ fontSize: "12px", lineHeight: 1.5 }}>
                Email the client both links from HQ, with replies coming back to you.
                <span className="block text-white/30" style={{ fontSize: "11px" }}>
                  Turn this off and only DocuSign's own signature request goes out — no
                  payment link reaches the client.
                </span>
              </span>
            </label>

            <div className="flex flex-wrap items-center gap-2.5 mt-6">
              <button
                type="submit"
                disabled={!canSend}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#8FA79B", color: "#000000", fontSize: "12.5px" }}
                data-testid="strategy-call-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
                {sending ? "Sending…" : "Send DocuSign + payment link"}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-5 py-2.5 rounded-full border border-white/15 text-white/60 hover:border-white/30 hover:text-white transition-colors disabled:opacity-40"
                style={{ fontSize: "12.5px" }}
                data-testid="strategy-call-cancel"
              >
                Cancel
              </button>
            </div>

            {/* Recent sends — the only place a rep can see who has signed and
                who has paid, so it lives in the same popup. */}
            <div className="mt-7 pt-5 border-t border-white/[0.07]">
              <p
                className="uppercase text-white/35 mb-3"
                style={{ fontSize: "10px", letterSpacing: "0.2em" }}
              >
                Recent Strategy Calls
              </p>
              {loadingRecent ? (
                <p className="text-white/30" style={{ fontSize: "12px" }}>
                  Loading…
                </p>
              ) : recent.length === 0 ? (
                <p className="text-white/30" style={{ fontSize: "12px" }}>
                  None sent yet.
                </p>
              ) : (
                <div className="space-y-2" data-testid="strategy-call-recent">
                  {recent.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-3 bg-white/[0.02] border border-white/[0.06] rounded-lg px-3.5 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-white/85" style={{ fontSize: "12.5px" }}>
                          {r.client_name}
                          {r.project_name ? (
                            <span className="text-white/35"> · {r.project_name}</span>
                          ) : null}
                        </p>
                        <p className="text-white/30 mt-0.5" style={{ fontSize: "11px" }}>
                          {fmtWhen(r.created_at)}
                          {r.payment_status === "paid" && r.payment_method
                            ? ` · paid by ${r.payment_method}`
                            : r.payment_status === "awaiting_wire"
                            ? " · wire on the way"
                            : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <StagePill label="Sent" done />
                        <StagePill label="Signed" done={r.docusign_status === "completed"} />
                        <StagePill label="Paid" done={r.payment_status === "paid"} tone="green" />
                      </div>
                      {/* Wire confirmation is a human act — a bank never calls a
                          webhook — so the rep needs this button. Card payments
                          settle themselves, so it is hidden for those. */}
                      {r.payment_status !== "paid" && (
                        <button
                          type="button"
                          onClick={() => confirmWire(r.id)}
                          disabled={confirmingWireId === r.id}
                          title={`Mark the ${DEPOSIT_LABEL} received by wire`}
                          className={`shrink-0 transition-colors disabled:opacity-40 p-1 ${
                            r.payment_status === "awaiting_wire"
                              ? "text-[#8FA79B] hover:text-white"
                              : "text-white/35 hover:text-white"
                          }`}
                          data-testid={`strategy-call-confirm-wire-${r.id}`}
                        >
                          {confirmingWireId === r.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Landmark className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => resend(r.id)}
                        disabled={resendingId === r.id}
                        title="Resend the email with both links"
                        className="shrink-0 text-white/35 hover:text-white transition-colors disabled:opacity-40 p-1"
                        data-testid={`strategy-call-resend-${r.id}`}
                      >
                        {resendingId === r.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Mail className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
